<script setup lang="ts">
import { computed } from 'vue';
import { IconDocument } from '@/components/design/icons';
import type { FileDropAction } from './actions/types';

interface Props {
  show: boolean;
  actions: FileDropAction[];
  files: File[];
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:show': [show: boolean];
  select: [action: FileDropAction];
}>();

const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});
</script>

<template>
  <n-modal
    v-model:show="showModal"
    preset="card"
    title="What do you want to do?"
    :style="{ maxWidth: '440px' }"
    :bordered="false"
  >
    <n-flex vertical size="large">
      <n-flex vertical>
        <n-button
          v-for="action in actions"
          :key="action.key"
          block
          size="large"
          @click="emit('select', action)"
        >
          <template v-if="action.icon" #icon>
            <n-icon :component="action.icon" />
          </template>
          {{ action.label }}
        </n-button>
      </n-flex>

      <n-list class="file-list" bordered size="small">
        <template #header>
          <n-text depth="3">
            {{ files.length }} file{{ files.length === 1 ? '' : 's' }} selected
          </n-text>
        </template>
        <n-list-item v-for="file in files" :key="file.name">
          <n-flex align="center" :size="8" :wrap="false" style="min-width: 0">
            <n-icon :component="IconDocument" :depth="3" />
            <n-ellipsis style="min-width: 0; flex: 1">{{ file.name }}</n-ellipsis>
          </n-flex>
        </n-list-item>
      </n-list>
    </n-flex>
  </n-modal>
</template>

<style scoped>
/* Naive's `.n-list-item__main` is `flex: 1` with the default `min-width: auto`,
   so a long (nowrap) file name resolves to a large min-content width and — since
   min-width beats max-width — widens the whole modal past its max-width instead of
   truncating. Force it to shrink so the inner ellipsis can engage. (No Naive prop
   exposes this internal, hence the :deep override.) */
.file-list :deep(.n-list-item__main) {
  min-width: 0;
}
</style>
